import React from 'react';
import { Device } from '../../lib/types';
import type { RescueDeviceSortColumn, SortDir } from '../../lib/types';
import { formatDaysAgo } from '../../lib/utils';
import ActionMenu, { type ActionMenuEntry } from '../../components/ActionMenu/ActionMenu';
import DeviceCell from './DeviceCell';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

/** Columns the stalled table can be sorted by (clicking their header). */
export type StalledSortKey = Extract<RescueDeviceSortColumn, 'adopter' | 'pet' | 'stalled'>;

// Action was 40% of this table — four buttons wide — for a section whose job is to show
// which adoptions have gone quiet. Behind one menu trigger it takes the tab's shared width,
// so it lines up with Pending above and Completed below; the lead columns fill the rest.
export const STALLED_COLUMNS: TableColumn<StalledSortKey>[] = [
  { label: 'Adopter', width: '26%', sortKey: 'adopter' },
  { label: 'Pet', width: '20%', sortKey: 'pet' },
  { label: 'Device', width: '25%' },
  { label: 'Stalled', width: '16%', sortKey: 'stalled' },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
];

interface StalledTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  /** Age threshold (in days) that qualifies a pending transfer as stalled. */
  thresholdDays: number;
  onPageChange: (page: number) => void;
  skeletonCount: number;
  sortKey: StalledSortKey;
  sortDir: SortDir;
  onSort: (key: StalledSortKey) => void;
  onMarkRecovered: (device: Device) => void;
  onNotRecovered: (device: Device) => void;
  onSendSms: (device: Device) => void;
  onCancelTransfer: (device: Device) => void;
}

export default function StalledTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  thresholdDays,
  onPageChange,
  skeletonCount,
  sortKey,
  sortDir,
  onSort,
  onMarkRecovered,
  onNotRecovered,
  onSendSms,
  onCancelTransfer,
}: StalledTransfersTableProps) {
  // Hide the section entirely when there are no stalled transfers and we're not
  // showing first-load skeletons. Keying off `loading` here would render an
  // empty "Stalled Transfers (0)" for the whole multi-page pending drain (which
  // keeps `loading` true); `skeletonCount` already covers the initial load.
  if (totalCount === 0 && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  // Chasing the adopter comes first — it's the step that might still save the adoption. Then
  // the two ways it ends, kept adjacent so they read as one either/or.
  function stalledActions(d: Device): ActionMenuEntry[] {
    return [
      { label: 'Send SMS', onSelect: () => onSendSms(d) },
      { label: 'Mark Recovered', onSelect: () => onMarkRecovered(d) },
      { label: 'Not Recovered', onSelect: () => onNotRecovered(d) },
      { label: 'Cancel Transfer', onSelect: () => onCancelTransfer(d), danger: true, dividerBefore: true },
    ];
  }

  return (
    <CollapsibleSection
      title="Stalled"
      count={totalCount}
      variant="warning"
      description={`Adoption transfers still awaiting activation after ${thresholdDays} days. Reach out to the adopter, or recover the device if the adoption fell through.`}
    >
      <div className={wrapperClass}>
        <table className={styles.table}>
          <TableHead columns={STALLED_COLUMNS} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={STALLED_COLUMNS.length} />
            ) : (
              devices.map((d) => (
                <tr key={d.id} className={styles.stalledRow}>
                  <td>
                    {d.adopterName}
                    <br />
                    <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                  </td>
                  <td>{d.petName ? <PetNameCell name={d.petName} photoThumbUrl={d.petPhotoThumbUrl} /> : '—'}</td>
                  <td>
                    <DeviceCell moduleId={d.id} type={d.type} />
                  </td>
                  <td>
                    <span className={styles.stalledAge}>{formatDaysAgo(d.transferDate)}</span>
                  </td>
                  <td>
                    <ActionMenu items={stalledActions(d)} ariaLabel={`Actions for ${d.petName ?? d.id}`} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
